<template>
  <div class="formgrid grid vertical-container">
    <div class="col-9">
      <Panel header="Список пользователей" class="mt-2">
        <ViewCustomData entityName="user" entitySpec="data_role" serviceName="BaseService" :columns="USER_COLUMNS"
          :dictionary="USER_DICTIONARY" @selectedRow="onSelectedRow" @permissions="onSetPermissions" ref="viewUserData"
          scrollHeight="575px" :paginator="false" mode="edit" :requiredFieldsTable="USER_REQUIRED_FILEDS"
          :prepareUpdate="prepareUpdate" :prepareInsert="prepareInsert" :beforeOpenNewRow="beforeOpenNewRow"
          :prepareData="prepareData" :virtualScrollerOptions="{ itemSize: 30 }">
          <template #toolbar>
            <Button :disabled="!selected || selected?.user_id < 0 || !permissions.setting" title="Сменить пароль"
              icon="pi pi-key" class="p-button-warning m-1" @click="showPasswordChange = true" label="Сменить пароль" />
            <Button :disabled="!selected || selected?.user_id < 0 || !permissions.setting" title="Устройства"
              icon="pi pi-mobile" class="p-button-warning m-1" @click="showDeviceChange = true" label="Устройства" />
          </template>
        </ViewCustomData>
      </Panel>
    </div>
    <div class="col-3">
      <Panel :header="`Разрешенные объекты ${selected?.user_name ?? ''}`" class="mt-2">
        <PermissionData :permissions="permissions"
          :userId="selected && selected.user_id > 0 ? selected.user_id : null" />
      </Panel>
    </div>
    <div class="col-3">
      <Panel :header="`Роли пользователя ${selected?.user_name ?? ''}`" class="mt-2">
        <UserRole :permissions="permissions" :userId="selected && selected.user_id > 0 ? selected.user_id : null" />
      </Panel>
    </div>
    <div class="col-4">
      <Panel :header="`Подразделение ${selected?.user_name ?? ''}`" class="mt-2">
        <UserDept :permissions="permissions" :userId="selected && selected.user_id > 0 ? selected.user_id : null" />
      </Panel>
    </div>
    <div class="col-5">
      <Panel header="Список ролей общий" class="mt-2">
        <RoleData />
      </Panel>
    </div>
  </div>
  <Dialog v-model:visible="showPasswordChange" :style="{ width: '550px' }" modal
    @hide="newPassword = ''; passwordLoading = false">
    <template #header>
      <i class="pi pi-exclamation-circle mr-3" style="font-size: 2rem; color: rgb(204,204,0)" />
      <div class="col-10 p-text-bold text-center" style="font-size: 20px">
        Смена пароля <strong>{{ selected.user_name }}</strong>
      </div>
    </template>
    <div v-if="passwordLoading" class="text-center">
      <ProgressSpinner />
    </div>
    <div v-else class="text-center">
      <div class="m-3">
        <Password id="newPassword" placeholder="Новый пароль" v-model="newPassword" inputClass="w-full"
          class="w-full" />
      </div>
      <template v-if="checkPasswordRules?.length">
        <Message v-for="rule in checkPasswordRules" :key="rule.text" :closable="false" :severity="rule.severity">
          {{ rule.text }}
        </Message>
      </template>
    </div>
    <template #footer>
      <Button label="Отмена" icon="pi pi-times" class="p-button-text" @click="showPasswordChange = false" />
      <Button label="Сменить" icon="pi pi-check" class="p-button-text" @click="passwordChange()"
        :disabled="invalidPasswordRules || !passwordRules || !newPassword.length" />
    </template>
  </Dialog>
  <Dialog v-model:visible="showDeviceChange" :style="{ width: '550px' }" modal>
    <template #header>
      <i class="pi pi-exclamation-circle mr-3" style="font-size: 2rem; color: rgb(204,204,0)" />
      <div class="col-10 p-text-bold text-center" style="font-size: 20px">
        Редактирование устройств <strong>{{ selected.user_name }}</strong>
      </div>
    </template>
    <div v-if="deviceLoading" class="text-center">
      <ProgressSpinner />
    </div>
    <div v-else class="text-center">
      <div class="mt-2">
        <Button label="Добавить андройд устройство" class="mt-2" severity="success" icon="pi pi-plus"
          style="width: 20vw" @click="onClickAddAndroid" />
      </div>
      <div class="mt-2">
        <Button label="Удалить устройство(самое старое)" class="mt-2" severity="danger" icon="pi pi-minus"
          style="width: 20vw" @click="onClickDeleteAndroid" />
      </div>
      <div class="mt-2">
        <Button label="Сбросить привязку к устройствам" class="mt-2" severity="warn" icon="pi pi-trash"
          style="width: 20vw" @click="onClickResetAndroid" />
      </div>
    </div>
  </Dialog>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useToast } from 'primevue/usetoast';

import PermissionData from './PermissionData.vue';
import UserRole from './UserRole.vue';
import RoleData from './RoleData.vue';
import UserDept from './UserDept.vue';
import { USER_DICTIONARY, USER_COLUMNS, USER_REQUIRED_FILEDS } from './userConstants';
import axios from 'axios';

const store = useStore();
const toast = useToast();

const passwordRules = ref(null);
const showPasswordChange = ref(false);
const newPassword = ref('');
const passwordLoading = ref(false);

const showDeviceChange = ref(null);
const deviceLoading = ref(null);

const selected = ref(null);
const permissions = ref({});
const viewUserData = ref(null);
const API_CONFIRM_SOUND = '123';
const invalidPasswordRules = computed(() => {
  if (!checkPasswordRules.value.length) return false;
  return checkPasswordRules.value.some(rule => rule.severity === 'error');
});

const checkPasswordRules = computed(() => {
  if (!passwordRules.value) return [];
  const result = [];
  for (const rule of passwordRules.value) {
    if (rule.regexRule) {
      const regex = new RegExp(rule.value, 'u');
      const isValid = regex.test(newPassword.value);
      result.push({
        text: rule.errorText,
        severity: isValid ? 'success' : 'error',
      });
    }
  }
  return result;
});

const passwordChange = () => {
  passwordLoading.value = true;
  const payload = {
    "targetUserLogin": selected.value.user_name,
    "newPassword": newPassword.value
  };
  const service = store.getters.nameToService('BaseClassicService');
  service
    .customPost('api/admin/password/change/', payload)
    .then(() => {
      toast.add({
        severity: 'success',
        summary: 'Пароль изменён',
        detail: 'Пароль пользователя успешно изменён',
        life: 3000,
      });
      showPasswordChange.value = false;
    })
    .catch(error => {
      store.commit('SET_ERROR', {
        er: error,
        source: ['UserData', 'submitHandler'],
      });
    })
    .finally(() => passwordLoading.value = false);
};

const onSelectedRow = (value) => {
  selected.value = value;
};

const onSetPermissions = (value) => {
  permissions.value = value;
};

const prepareUpdate = (rowData) => {
  delete rowData.user_password_hash
  delete rowData.user_role;
  delete rowData.user_device_count;
  return rowData;
};

const prepareInsert = (rowData) => {
  delete rowData.user_id;
  delete rowData.user_password_hash;
  delete rowData.user_device_count;
  return rowData;
};

const beforeOpenNewRow = (rowData) => {
  rowData.android_access = false;
  rowData.user_actual = true;
};

const loadRules = () => {
  const service = store.getters.nameToService('BaseClassicService');
  service
    .customGet('api/password/rules/', {})
    .then(response => (passwordRules.value = response))
    .catch(error => {
      store.commit('SET_ERROR', {
        er: error,
        source: ['UserData', 'loadRules'],
      });
    });
};

const prepareData = (data) => {
  const newData = data.map(item => ({
    ...item,
    user_password_hash: item.user_password ? true : false
  }))
  if (newData.length) {
    if (viewUserData.value) {
      viewUserData.value.selected = newData[0];
    }
    selected.value = newData[0];
  }
  return newData;
};

const onClickAddAndroid = (rowData) => {
  const headers = {
    Authorization: 'Basic ' + btoa(store.getters.info.login + ':' + store.getters.password),
  };
  const API = `${store.getters.server('TM_MIDDLE')}${API_CONFIRM_SOUND}`;
  axios
    .post(API, { eventId: selected.value, userId: store.getters.info.userId }, { headers })
    .then(() => {
      toast.add({
        severity: 'success',
        summary: 'Устройство добавлено',
        detail: 'Android-устройство успешно зарегистрировано для пользователя',
        life: 3000,
      });
    })
    .catch((er) => {
      store.commit('SET_ERROR', {
        er: er?.response ?? er,
        source: ['UserData', 'onClickAddAndroid'],
      });
    });
};

const onClickDeleteAndroid = (rowData) => {
  const headers = {
    Authorization: 'Basic ' + btoa(store.getters.info.login + ':' + store.getters.password),
  };
  const API = `${store.getters.server('TM_MIDDLE')}${API_CONFIRM_SOUND}`;
  axios
    .post(API, { eventId: selected.value, userId: store.getters.info.userId }, { headers })
    .then(() => {
      toast.add({
        severity: 'success',
        summary: 'Устройство удалено',
        detail: 'Android-устройство успешно удалено из привязок пользователя',
        life: 3000,
      });
    })
    .catch((er) => {
      store.commit('SET_ERROR', {
        er: er?.response ?? er,
        source: ['UserData', 'onClickDeleteAndroid'],
      });
    });
};

const onClickResetAndroid = (rowData) => {
  const headers = {
    Authorization: 'Basic ' + btoa(store.getters.info.login + ':' + store.getters.password),
  };
  const API = `${store.getters.server('TM_MIDDLE')}${API_CONFIRM_SOUND}`;
  axios
    .post(API, { eventId: selected.value, userId: store.getters.info.userId }, { headers })
    .then(() => {
      toast.add({
        severity: 'success',
        summary: 'Привязка сброшена',
        detail: 'Связь устройства с пользователем успешно сброшена',
        life: 3000,
      });
    })
    .catch((er) => {
      store.commit('SET_ERROR', {
        er: er?.response ?? er,
        source: ['UserData', 'onClickResetAndroid'],
      });
    });
};

onMounted(() => {
  loadRules();
});
</script>

<style lang="scss" scoped>
::v-deep(.p-panel .p-panel-header) {
  padding: 0.5rem;
  border: none;
  background: #84abd6 !important;
  flex-direction: column !important;
}

::v-deep(.p-panel-content) {
  padding: 0.2rem;
}

::v-deep(.p-message) {
  margin: 1px;
}

::v-deep(.p-message-wrapper) {
  padding: 0.4rem 1rem;
}
</style>